/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import React from 'react';
import { connect } from 'react-redux';
import {
    IMultiAxisDataServiceDataLayout, IMultiAxisDataServiceDataLayoutMemberExpansion,
    IMultiAxisDataset,
    IMultiAxisDatasetHandle
} from '../store/types/MultiAxisDatasetTypes';
import { IVisualizationDynamicProperties, IVisualizationTypeProperties } from '../store/types/VisualizationTypes';
import { DrillDownInfo, ILinkOptions, IState } from '../store/types/StateTypes';
import datasetTypes from '../enums/datasetTypes';
import {
    loadMultiAxisData,
    drillDownTo as drillDownToAction,
    setLevelExpandedState as setLevelExpandedStateAction,
    setMemberExpandedState as setMemberExpandedStateAction,
    setMembersExpandedState as setMembersExpandedStateAction
} from '../actions/multiAxisDataActions';
import { updateDynamicProperties as updateDynamicPropertiesAction } from '../actions/visualizationActions';
import DataState from '../enums/DataState';
import i18n from '../../i18n/AdHocBundle.properties';
import AdHocMessageComponent from './AdHocMessageComponent';

interface IMultiAxisDataServiceComponentProperties {
    visualization: any, // TODO: Component<IMultiAxisDatasetVisualization>
    controlsDataLoading: boolean,
    data: IMultiAxisDataset,
    staticProperties: any,
    dynamicProperties: IVisualizationDynamicProperties,
    levelsLayout: any,
    linkOptions?: ILinkOptions,
    eventCallbacks?: any,
    dataState: string,
    dataError: null | any,
    deferredAction: JQuery.Deferred<any>,

    loadData: (dataLayout: IMultiAxisDataServiceDataLayout, startIndexRows: number, startIndexColumns: number, countRows: number, countColumns: number) => void,
    updateDynamicProperties: Function,
    setLevelExpandedState: Function,
    setMemberExpandedState: Function,
    setMembersExpandedState: Function,
    drillDownTo?: (info: DrillDownInfo) => void
}

class MultiAxisDataServiceComponentUnconnected
    extends React.Component<IMultiAxisDataServiceComponentProperties, never> {

    componentDidMount(): void {
        this.resolveDeferredAction();
    }

    componentDidUpdate(prevProps: Readonly<IMultiAxisDataServiceComponentProperties>, prevState: Readonly<never>, snapshot?: any): void {
        this.resolveDeferredAction();
    }

    resolveDeferredAction() {
        const { dataState, dataError, deferredAction } = this.props;

        if (dataState === DataState.LOADED) {
            deferredAction.resolve();
        } else if (dataState === DataState.ERROR) {
            deferredAction.reject(dataError);
        }
    }

    render() {
        const {
            visualization: Visualization, updateDynamicProperties, data, controlsDataLoading, linkOptions, staticProperties, dynamicProperties, loadData,
            setLevelExpandedState, setMemberExpandedState, setMembersExpandedState, eventCallbacks, drillDownTo
        } = this.props;

        const handle: IMultiAxisDatasetHandle = {
            requestDatasetUpdate: loadData,
            updateDynamicProperties: (props: any) => {
                if (Object.keys(props).length) {
                    updateDynamicProperties(props);
                }
            },
            updateLevelExpansion: setLevelExpandedState,
            updateMemberExpansion: setMemberExpandedState,
            updateMemberExpansions: setMembersExpandedState,
            drillDownTo: (info: DrillDownInfo) => {
                if (drillDownTo) {
                    if (eventCallbacks && eventCallbacks.onDrilldown) {
                        eventCallbacks.onDrilldown.call(null, info);
                    }

                    drillDownTo(info);
                }
            }
        };

        if (!controlsDataLoading && !data.summary.complete) {
            // TODO optimize
            handle.requestDatasetUpdate(data.summary as any, 0, 0, 0, 0);
        }

        if (data.summary.empty) {
            const messageKey = data.summary.columns.items.concat(data.summary.rows.items).find((item: any) => (item.measuresLevel))
                ? 'adhoc.data.is.empty.text' : 'adhoc.data.is.empty.text.no.measures';

            return (
                <AdHocMessageComponent
                    message={i18n[messageKey]}
                />
            )
        }

        return (
            <Visualization
                dataset={data}
                staticProperties={staticProperties}
                dynamicProperties={dynamicProperties}
                linkOptions={linkOptions}
                handle={handle}
            />
        );
    }
}

const mapStateToProps = (state: IState) => {
    const visualization: IVisualizationTypeProperties = state.visualization[state.visualizationDescriptor.name];
    return {
        levelsLayout: state.levelsLayout,
        linkOptions: state.linkOptions,
        eventCallbacks: state.eventCallbacks,
        data: state.data[datasetTypes.MULTI_AXIS],
        staticProperties: visualization.staticProperties,
        dynamicProperties: visualization.dynamicProperties,
        dataState: state.dataState,
        dataError: state.dataError,
        deferredAction: state.deferredAction
    };
};

const mapDispatchToProps = (dispatch: any) => ({
    loadData: (dataLayout: IMultiAxisDataServiceDataLayout, startIndexRows: number, startIndexColumns: number, countRows: number, countColumns: number) => (
        dispatch(loadMultiAxisData(dataLayout, startIndexRows, startIndexColumns, countRows, countColumns))
    ),
    updateDynamicProperties: (dynamicProperties: any) => {
        dispatch(updateDynamicPropertiesAction(dynamicProperties));
    },
    setLevelExpandedState: (levelId: string, expanded: boolean, dynamicProperties: any) => {
        dispatch(setLevelExpandedStateAction(levelId, expanded, dynamicProperties))
    },
    setMemberExpandedState: (memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion, isColumns: boolean, dynamicProperties: any) => {
        dispatch(setMemberExpandedStateAction(memberExpansion, isColumns, dynamicProperties))
    },
    setMembersExpandedState: (rowExpansions: IMultiAxisDataServiceDataLayoutMemberExpansion[], colExpansions: IMultiAxisDataServiceDataLayoutMemberExpansion[]) => {
        dispatch(setMembersExpandedStateAction(rowExpansions, colExpansions))
    },
    drillDownTo: (info: DrillDownInfo) => {
        dispatch(drillDownToAction(info))
    }

});

const connectFn = connect(mapStateToProps, mapDispatchToProps);
const MultiAxisDataServiceComponent = connectFn(MultiAxisDataServiceComponentUnconnected);

export {
    MultiAxisDataServiceComponent,
    MultiAxisDataServiceComponentUnconnected,
    mapDispatchToProps,
    mapStateToProps
};
